import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { Check, ChevronRight } from "@/components/icons"
import { useTrustedSourcesSelection } from "@/features/trusted-sources/hooks/use-trusted-sources-selection"
import { useSubmenuPositioning } from "@/hooks/use-submenu-positioning"

type TrustedSourcesSubmenuProps = {
  sessionType: string
  onSelectAndClose: () => void
}

/**
 * Inline hover-based trusted sources submenu
 * Used in text selection menu and other contextual menus
 */
export function TrustedSourcesSubmenu({
  sessionType,
  onSelectAndClose,
}: TrustedSourcesSubmenuProps) {
  const { t } = useTranslation()
  const {
    personalSources,
    companySources,
    selectedUrls,
    isAllSelected,
    toggleSource,
    toggleAll,
    trustedMode,
    setTrustedMode,
  } = useTrustedSourcesSelection(sessionType)

  const [hoveredMenu, setHoveredMenu] = useState<"personal" | "company" | "mode" | null>(null)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const { menuRef, getSubmenuClasses } = useSubmenuPositioning({
    submenuWidth: 256,
    gap: 4,
    trigger: true,
  })

  const handleMouseEnter = useCallback((menu: "personal" | "company" | "mode") => {
    // Cancel any pending close
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    setHoveredMenu(menu)
  }, [])

  const handleMouseLeave = useCallback(() => {
    // Delay closing to allow moving to submenu
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null)
    }, 300) // 300ms grace period
  }, [])

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [])

  const submenuClasses = getSubmenuClasses(
    "z-[10001] w-64 rounded-md border bg-popover p-1 text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95"
  )

  return (
    <div
      ref={menuRef}
      className="bg-popover text-popover-foreground animate-in fade-in-0 zoom-in-95 w-64 rounded-md border p-1 shadow-lg"
    >
      {/* All Trusted */}
      <button
        className="hover:bg-accent hover:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none focus:bg-transparent active:bg-transparent"
        onClick={(e) => {
          e.stopPropagation()
          toggleAll()
          e.currentTarget.blur()
        }}
      >
        <div className="flex h-4 w-4 items-center justify-center">
          {isAllSelected && <Check className="h-4 w-4" />}
        </div>
        <span>{t("sources.all_trusted", { defaultValue: "All Trusted" })}</span>
      </button>

      <div className="bg-border -mx-1 my-1 h-px" />

      {/* Personal Sources */}
      {personalSources.length > 0 && (
        <div
          className="relative"
          onMouseEnter={() => handleMouseEnter("personal")}
          onMouseLeave={handleMouseLeave}
        >
          <button className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none">
            <span>{t("sources.personal_sources", { defaultValue: "Personal Sources" })}</span>
            <ChevronRight className="ml-auto h-4 w-4" />
          </button>
          {hoveredMenu === "personal" && (
            <div
              className={submenuClasses}
              onMouseEnter={() => handleMouseEnter("personal")}
              onMouseLeave={handleMouseLeave}
            >
              {personalSources.map((source) => {
                const isChecked = selectedUrls.includes(source.url)
                return (
                  <button
                    key={source.id}
                    className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleSource(source.url, !isChecked)
                    }}
                  >
                    <div className="flex h-4 w-4 items-center justify-center">
                      {isChecked && <Check className="h-4 w-4" />}
                    </div>
                    <span>{source.name}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Company Sources */}
      {companySources.length > 0 && (
        <div
          className="relative"
          onMouseEnter={() => handleMouseEnter("company")}
          onMouseLeave={handleMouseLeave}
        >
          <button className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none">
            <span>{t("sources.company_sources", { defaultValue: "Company Sources" })}</span>
            <ChevronRight className="ml-auto h-4 w-4" />
          </button>
          {hoveredMenu === "company" && (
            <div
              className={submenuClasses}
              onMouseEnter={() => handleMouseEnter("company")}
              onMouseLeave={handleMouseLeave}
            >
              {companySources.map((source) => {
                const isChecked = selectedUrls.includes(source.url)
                return (
                  <button
                    key={source.id}
                    className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleSource(source.url, !isChecked)
                    }}
                  >
                    <div className="flex h-4 w-4 items-center justify-center">
                      {isChecked && <Check className="h-4 w-4" />}
                    </div>
                    <span>{source.name}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      <div className="bg-border -mx-1 my-1 h-px" />

      {/* Mode Selection */}
      <div
        className="relative"
        onMouseEnter={() => handleMouseEnter("mode")}
        onMouseLeave={handleMouseLeave}
      >
        <button className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none">
          <span>{t("sources.mode", { defaultValue: "Mode" })}</span>
          <ChevronRight className="ml-auto h-4 w-4" />
        </button>
        {hoveredMenu === "mode" && (
          <div
            className={submenuClasses}
            onMouseEnter={() => handleMouseEnter("mode")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none"
              onClick={(e) => {
                e.stopPropagation()
                setTrustedMode("only")
              }}
            >
              <div className="flex h-4 w-4 items-center justify-center">
                {trustedMode === "only" && <Check className="h-4 w-4" />}
              </div>
              <span>{t("sources.trusted_only", { defaultValue: "Trusted Only" })}</span>
            </button>
            <button
              className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors outline-none select-none"
              onClick={(e) => {
                e.stopPropagation()
                setTrustedMode("preferred")
              }}
            >
              <div className="flex h-4 w-4 items-center justify-center">
                {trustedMode === "preferred" && <Check className="h-4 w-4" />}
              </div>
              <span>{t("sources.trusted_preferred", { defaultValue: "Trusted Preferred" })}</span>
            </button>
          </div>
        )}
      </div>

      <div className="bg-border -mx-1 my-1 h-px" />

      {/* Apply and close */}
      <button
        className="border-foreground bg-primary text-primary-foreground hover:bg-primary/90 focus:bg-primary/90 relative flex w-full cursor-default items-center justify-center gap-2 rounded-md border px-2 py-2 text-sm font-medium transition-colors outline-none select-none"
        onClick={onSelectAndClose}
      >
        <span>{t("sources.apply_and_search", { defaultValue: "Apply & Search" })}</span>
      </button>
    </div>
  )
}
